import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ScopeItemRow } from "@/api/scope-activation";
import { approveScope, listScopeItems, reopenScope } from "@/api/scope-activation";
import { useCalibration } from "@/hooks/useCalibration";
import { diagnosticKeys } from "@/hooks/useDiagnostic";
import { formatCount } from "@/lib/format";
import { formatDate } from "@/lib/datetime";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

import { notePreview } from "./aderenciaData";

/**
 * Resultado consolidado: de forma simples, tudo que **efetivamente será ativado**
 * (veredito efetivo = activate), agrupado por LoB. É a visão que o analista
 * apresenta e onde ele **aprova** (congela) o escopo do Functional.
 */
export function ConsolidatedView({
  projectId,
  executionId,
  approved = false,
  hasUnsaved = false,
  approvalVersion,
  approvedAt,
  hasReadyExtraction = false,
}: {
  projectId?: string;
  executionId?: string;
  approved?: boolean;
  hasUnsaved?: boolean;
  approvalVersion?: number | null;
  approvedAt?: string | null;
  // Gate 3 da catraca: Configuration Server já extraiu (ready) este escopo.
  // Reabrir fica travado na UI — o backend também recusa com 409.
  hasReadyExtraction?: boolean;
}) {
  const { t } = useTranslation();
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const queryClient = useQueryClient();
  // Com Calibragem publicada, o Diagnóstico mostra a foto dela: a aprovação
  // nova só chega lá quando a Calibragem for reconfirmada (21/10/2026). 404 é
  // "sem calibragem", e aí reabrir segue como sempre.
  const calibration = useCalibration(projectId);
  const calibrationPublished = calibration.data?.status === "published";

  // O que será ativado = veredito efetivo "activate" (disponível no país).
  const { data: page, isFetching } = useQuery({
    queryKey: ["scope-items", projectId, executionId, "consolidated"],
    queryFn: () =>
      listScopeItems(projectId!, {
        executionId: executionId!,
        page: 1,
        limit: 200,
        verdict: "activate",
        countryOnly: true,
      }),
    enabled: !!projectId && !!executionId,
  });

  const items = page?.items ?? [];
  const total = page?.total ?? 0;

  // Aprovar e reabrir trocam a base do tile do Ato 1 (escopo aprovado ou
  // curadoria salva), então o snapshot do Diagnóstico também vence (#1344).
  const invalidateApproval = () => {
    queryClient.invalidateQueries({ queryKey: ["scope-approval", projectId] });
    queryClient.invalidateQueries({
      queryKey: ["scope-items", projectId, executionId],
    });
    if (projectId) {
      queryClient.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId) });
    }
  };
  const approve = useMutation({
    mutationFn: () => approveScope(projectId!, executionId!),
    onSuccess: invalidateApproval,
  });
  const reopen = useMutation({
    mutationFn: () => reopenScope(projectId!),
    onSuccess: invalidateApproval,
  });

  // Agrupa por LoB (ordem alfabética; grupo maior primeiro).
  const byLob = new Map<string, ScopeItemRow[]>();
  for (const i of items) {
    const k = i.lob || "—";
    const arr = byLob.get(k);
    if (arr) arr.push(i);
    else byLob.set(k, [i]);
  }
  const groups = [...byLob.entries()].sort(
    (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]),
  );

  return (
    <div className="flex flex-col gap-4">
      {/* headline + ação de aprovar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold tracking-tight">
              {formatCount(total)}
            </span>
            <span className="text-sm text-muted-foreground">
              {t("scope items will be activated")}
              {isFetching ? " · …" : ""}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t(
              "The consolidated Functional scope (effective verdict = Activate) that goes forward to the next agents.",
            )}
          </p>
        </div>

        {approved ? (
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                <ShieldCheck className="h-4 w-4" />
                {t("Functional scope approved · v{{version}}", { version: approvalVersion })}
                {approvedAt ? ` · ${formatDate(approvedAt)}` : ""}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                disabled={hasReadyExtraction || reopen.isPending}
                title={
                  hasReadyExtraction
                    ? t(
                        "The configuration has already been extracted with this scope — reopening requires duplicating the project",
                      )
                    : t("Reopen the scope for editing (dev; admin-only in the future)")
                }
                onClick={() => (calibrationPublished ? setConfirmReopen(true) : reopen.mutate())}
              >
                {t("Reopen")}
              </Button>
            </div>
            {hasReadyExtraction && (
              <p className="m-0 text-[11px] text-muted-foreground">
                {t(
                  "The configuration has already been extracted with this scope. Reopening the scope items requires duplicating the project.",
                )}
              </p>
            )}
          </div>
        ) : (
          projectId &&
          executionId && (
            <Button
              variant="action"
              className="gap-1.5"
              disabled={hasUnsaved || approve.isPending}
              title={
                hasUnsaved
                  ? t("Save the draft (Catalog) before approving")
                  : t("Approve and freeze the Functional scope")
              }
              onClick={() => setConfirmApprove(true)}
            >
              <ShieldCheck className="h-4 w-4" /> {t("Approve scope")}
            </Button>
          )
        )}
      </div>

      {hasUnsaved && !approved && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {t("There are unsaved verdict changes in the Catalog tab. Save the draft before approving.")}
        </div>
      )}

      {/* lista simples do que será ativado, por LoB */}
      <div className="flex flex-col gap-4">
        {groups.map(([lob, rows]) => (
          <div key={lob} className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-bold">{lob}</h3>
              <span className="font-mono text-[11px] font-bold text-emerald-600">
                {rows.length}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {rows.map((i) => (
                <div
                  key={i.scope_item_id}
                  data-testid={`consolidated-item-${i.scope_item_id}`}
                  className="flex flex-col gap-0.5 rounded border bg-white px-3 py-1.5 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold">
                      {i.scope_item_id}
                    </span>
                    <span className="flex-1 truncate text-muted-foreground">
                      {i.scope_item_name || "-"}
                    </span>
                    {i.suggestion_reason && (
                      <span className="text-[10px] text-muted-foreground/80">
                        {i.suggestion_reason}
                      </span>
                    )}
                  </div>
                  {i.curation_note && (
                    <div className="flex items-baseline gap-2 text-[11px]">
                      <span className="truncate italic text-muted-foreground" title={i.curation_note}>
                        {notePreview(i.curation_note)}
                      </span>
                      {i.show_in_report === false && (
                        <span className="shrink-0 text-[10px] text-amber-700">
                          {t("Internal note (not in report)")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="rounded-lg border bg-white p-6 text-center text-xs text-muted-foreground">
            {isFetching
              ? t("Loading…")
              : t("Nothing to activate yet — confirm verdicts in the Catalog.")}
          </div>
        )}
      </div>

      <AlertDialog open={confirmReopen} onOpenChange={setConfirmReopen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Reopen the scope?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "This project has a published calibration. When you approve the new scope, the calibration needs to be confirmed again; until then the Diagnostic keeps showing the published version.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => reopen.mutate()}>{t("Reopen")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmApprove} onOpenChange={setConfirmApprove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Approve the Functional scope?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "This freezes the Functional scope: the verdict and a new Functional run get locked. Other agents (e.g. Architect) are unaffected — to redo the analysis, duplicate the project.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => approve.mutate()}>
              {t("Approve scope")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
